import type { MessageKey } from '@dijitalapp/i18n';
import type { AdminInviteDto, AdminMeDto, AdminReportDto, AdminSchoolRequestDto, AdminUserDto } from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { AiUsageTab, AuditTab, ConfigTab, SecurityTab, SystemTab } from '../components/AdminOps';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

type Tab = 'users' | 'reports' | 'invites' | 'schools' | 'system' | 'config' | 'audit' | 'security' | 'ai';

function useAction() {
  const { i18n } = useApp();
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(i18n.t(errorMessageKey(err)));
    }
  };
  return { error, run };
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p className="alert alert-error" role="alert">
      {error}
    </p>
  ) : null;
}

function UsersTab({ canSuspend }: { canSuspend: boolean }) {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const { error, run } = useAction();
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);

  const search = (e?: FormEvent) => {
    e?.preventDefault();
    void run(async () => setUsers((await api<{ users: AdminUserDto[] }>('GET', `/admin/users?q=${encodeURIComponent(q)}`)).users));
  };

  const toggle = (u: AdminUserDto) =>
    run(async () => {
      if (u.accountState === 'suspended') await api('POST', `/admin/users/${u.id}/unsuspend`);
      else {
        const reason = window.prompt(t('admin.suspendReason'))?.trim();
        if (!reason) return;
        await api('POST', `/admin/users/${u.id}/suspend`, { reason });
      }
      search();
    });

  return (
    <section className="card stack">
      <form className="row" onSubmit={search} style={{ flexWrap: 'nowrap' }}>
        <label className="visually-hidden" htmlFor="admin-q">
          {t('admin.search')}
        </label>
        <input id="admin-q" className="input" placeholder={t('admin.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-primary">{t('chat.search')}</button>
      </form>
      <ErrorLine error={error} />
      {users?.length === 0 && <p className="muted">{t('admin.noResults')}</p>}
      {users?.map((u) => (
        <div key={u.id} className="toggle-row">
          <div style={{ minWidth: 0 }}>
            <strong>{u.displayName}</strong> <span className="muted">@{u.username}</span>{' '}
            <span className="badge">{t(`admin.state.${u.accountState}` as MessageKey)}</span>{' '}
            {u.ageBand === 'minor' && <span className="badge">13–17</span>} {u.roles.map((r) => <span key={r} className="badge">{r}</span>)}
            <p className="muted truncate">
              {u.email ?? '—'} · {formatDate(u.createdAt, { dateStyle: 'medium' })}
            </p>
          </div>
          <span className="spacer" />
          {canSuspend && (
            <button className="btn btn-danger" type="button" onClick={() => void toggle(u)}>
              {u.accountState === 'suspended' ? t('admin.unsuspend') : t('admin.suspend')}
            </button>
          )}
        </div>
      ))}
    </section>
  );
}

function ReportsTab() {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const { error, run } = useAction();
  const [reports, setReports] = useState<AdminReportDto[] | null>(null);
  const load = () => api<{ reports: AdminReportDto[] }>('GET', '/admin/reports').then((r) => setReports(r.reports));
  useEffect(() => {
    void api<{ reports: AdminReportDto[] }>('GET', '/admin/reports').then((r) => setReports(r.reports));
  }, []);

  const resolve = (r: AdminReportDto, decision: 'actioned' | 'dismissed') =>
    run(async () => {
      const note = window.prompt(t('admin.reports.note'))?.trim();
      if (!note) return;
      await api('POST', `/admin/reports/${r.id}/resolve`, { decision, note });
      await load();
    });

  return (
    <section className="stack">
      <ErrorLine error={error} />
      {reports?.length === 0 && <p className="muted">{t('admin.reports.empty')}</p>}
      {reports?.map((r) => (
        <article key={r.id} className="card stack" style={{ gap: 'var(--space-2)' }}>
          <div className="row">
            <span className="badge">{t('admin.reports.priority', { priority: r.priority })}</span>
            <strong>{t(`report.reason.${r.reason}` as MessageKey)}</strong>
            <span className="spacer" />
            <span className="muted">{formatDate(r.createdAt)}</span>
          </div>
          <p className="muted">
            @{r.reporter.username} → @{r.target.username} ({r.targetType})
          </p>
          {r.details && <p>{r.details}</p>}
          {r.evidence.length > 0 && (
            <details>
              <summary>
                {t('admin.reports.evidence')} ({r.evidence.length})
              </summary>
              <ol className="stack" style={{ gap: 'var(--space-1)', marginTop: 'var(--space-2)' }}>
                {r.evidence.map((e) => (
                  <li key={e.messageId} className="bubble-text">
                    <span className="muted">{e.senderId === r.target.id ? `@${r.target.username}` : `@${r.reporter.username}`}:</span>{' '}
                    {e.body}
                  </li>
                ))}
              </ol>
            </details>
          )}
          <div className="row">
            <span className="spacer" />
            <button className="btn" type="button" onClick={() => void resolve(r, 'dismissed')}>
              {t('admin.reports.dismiss')}
            </button>
            <button className="btn btn-danger" type="button" onClick={() => void resolve(r, 'actioned')}>
              {t('admin.reports.action')}
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}

/** Staff review of school requests (ADR-019): approval creates a verified school space run by the requester. */
function SchoolsTab() {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const { error, run } = useAction();
  const [requests, setRequests] = useState<AdminSchoolRequestDto[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const load = () => api<{ requests: AdminSchoolRequestDto[] }>('GET', '/admin/school-requests').then((r) => setRequests(r.requests));
  useEffect(() => {
    void api<{ requests: AdminSchoolRequestDto[] }>('GET', '/admin/school-requests').then((r) => setRequests(r.requests));
  }, []);
  const decide = (id: string, decision: 'approve' | 'reject') => {
    if (decision === 'approve' && !window.confirm(t('admin.schools.approveConfirm'))) return;
    void run(async () => {
      await api('POST', `/admin/school-requests/${id}/${decision}`, { note: notes[id] || undefined });
      await load();
    });
  };
  return (
    <section className="stack">
      <p className="hint">{t('admin.schools.note')}</p>
      <ErrorLine error={error} />
      {requests.length === 0 && <p className="muted">{t('admin.schools.none')}</p>}
      {requests.map((r) => (
        <div key={r.id} className="card stack">
          <div>
            <strong>{r.schoolName}</strong> <span className="muted">· {r.city} · {formatDate(r.createdAt)}</span>
            <br />
            <span className="muted">
              {r.requester.displayName} (@{r.requester.username})
            </span>
            {r.note && <p style={{ margin: 0 }}>{r.note}</p>}
          </div>
          <div className="row">
            <label className="visually-hidden" htmlFor={`sn-${r.id}`}>
              {t('admin.schools.reviewNote')}
            </label>
            <input
              id={`sn-${r.id}`}
              className="input"
              style={{ flex: 1, minWidth: 160 }}
              maxLength={1000}
              placeholder={t('admin.schools.reviewNote')}
              value={notes[r.id] ?? ''}
              onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
            />
            <button className="btn btn-primary" type="button" onClick={() => decide(r.id, 'approve')}>
              {t('admin.schools.approve')}
            </button>
            <button className="btn btn-danger" type="button" onClick={() => decide(r.id, 'reject')}>
              {t('admin.schools.reject')}
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

function InvitesTab() {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const { error, run } = useAction();
  const [invites, setInvites] = useState<AdminInviteDto[]>([]);
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const load = () => api<{ invites: AdminInviteDto[] }>('GET', '/admin/invites').then((r) => setInvites(r.invites));
  useEffect(() => {
    void api<{ invites: AdminInviteDto[] }>('GET', '/admin/invites').then((r) => setInvites(r.invites));
  }, []);

  const create = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      setCodes((await api<{ codes: string[] }>('POST', '/admin/invites', { count, note })).codes);
      await load();
    });
  };

  return (
    <section className="stack">
      <form className="card row" onSubmit={create}>
        <label htmlFor="inv-count">{t('admin.invites.count')}</label>
        <input id="inv-count" className="input" type="number" min={1} max={50} value={count} onChange={(e) => setCount(Number(e.target.value))} style={{ width: 90 }} />
        <label htmlFor="inv-note">{t('admin.invites.note')}</label>
        <input id="inv-note" className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
        <button className="btn btn-primary">{t('admin.invites.create')}</button>
      </form>
      <ErrorLine error={error} />
      {codes.length > 0 && (
        <div className="alert alert-info">
          <p>{t('admin.invites.newCodes')}</p>
          <pre style={{ margin: 0, userSelect: 'all' }}>{codes.join('\n')}</pre>
        </div>
      )}
      <div className="card">
        {invites.map((i) => (
          <div key={i.id} className="toggle-row">
            <div>
              <strong>{i.note || '—'}</strong>
              <p className="muted">
                {t('admin.invites.used', { used: i.usedCount, max: i.maxUses })} · {formatDate(i.createdAt, { dateStyle: 'medium' })}
                {i.expiresAt ? ` → ${formatDate(i.expiresAt, { dateStyle: 'medium' })}` : ''}
              </p>
            </div>
            <span className="spacer" />
            {i.revokedAt ? (
              <span className="badge">{t('admin.invites.revoked')}</span>
            ) : (
              <button className="btn btn-danger" type="button" onClick={() => void run(async () => { await api('POST', `/admin/invites/${i.id}/revoke`); await load(); })}>
                {t('admin.invites.revoke')}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Staff console. Every call is authorized server-side; this page only hides what the API would refuse. */
export function AdminPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [me, setMe] = useState<AdminMeDto | null | 'none'>(null);
  const [tab, setTab] = useState<Tab>('users');

  useEffect(() => {
    void api<AdminMeDto>('GET', '/admin/me').then(setMe, () => setMe('none'));
  }, []);

  if (me === null) return <p className="muted">{t('app.loading')}</p>;
  if (me === 'none') return <p className="muted">{t('error.NOT_FOUND')}</p>;

  const needs: Record<Tab, string> = {
    users: 'users.read',
    reports: 'reports.manage',
    invites: 'invites.manage',
    schools: 'schools.verify',
    system: 'system.read',
    config: 'config.manage',
    audit: 'audit.read',
    security: 'security.read',
    ai: 'ai.read',
  };
  const tabs: Tab[] = (['users', 'reports', 'invites', 'schools', 'system', 'config', 'audit', 'security', 'ai'] as Tab[]).filter((x) =>
    (me.permissions as string[]).includes(needs[x]),
  );

  return (
    <div className="content-inner">
      <h1>{t('admin.title')}</h1>
      <p className="muted">{t('admin.roles', { roles: me.roles.join(', ') })}</p>
      {!me.sessionEligible ? (
        <p className="alert alert-info" role="status">
          {t('admin.passkeyRequired')}
        </p>
      ) : (
        <>
          <div className="row" role="tablist">
            {tabs.map((x) => (
              <button key={x} type="button" role="tab" aria-selected={tab === x} className={`btn ${tab === x ? 'btn-primary' : ''}`} onClick={() => setTab(x)}>
                {t(`admin.tab.${x}` as MessageKey)}
              </button>
            ))}
          </div>
          {tab === 'users' && <UsersTab canSuspend={me.permissions.includes('users.suspend')} />}
          {tab === 'reports' && <ReportsTab />}
          {tab === 'invites' && <InvitesTab />}
          {tab === 'schools' && <SchoolsTab />}
          {tab === 'system' && <SystemTab />}
          {tab === 'config' && <ConfigTab />}
          {tab === 'audit' && <AuditTab />}
          {tab === 'security' && <SecurityTab />}
          {tab === 'ai' && <AiUsageTab />}
        </>
      )}
    </div>
  );
}
